import React, { useEffect, useState } from 'react';
import API from '../api/axios';
import Header from '../components/Header';
import {
  Users, ShoppingBag, IndianRupee, Clock, CheckCircle, TrendingUp, Star
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const STATUS_COLORS = { pending: '#F59E0B', confirmed: '#3B82F6', in_progress: '#8B5CF6', completed: '#10B981', cancelled: '#EF4444' };
const STATUS_LABELS = { pending: 'Pending', confirmed: 'Confirmed', in_progress: 'In Progress', completed: 'Completed', cancelled: 'Cancelled' };

const MONTHLY = [
  { month: 'Jan', revenue: 45000, bookings: 38 },
  { month: 'Feb', revenue: 52000, bookings: 45 },
  { month: 'Mar', revenue: 48000, bookings: 41 },
  { month: 'Apr', revenue: 61000, bookings: 58 },
  { month: 'May', revenue: 75000, bookings: 72 },
  { month: 'Jun', revenue: 82000, bookings: 88 },
];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/bookings/dashboard/').then(r => setStats(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex-1 flex items-center justify-center">
      <div className="animate-spin w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full" />
    </div>
  );

  const statCards = [
    { label: 'Total Revenue', value: `₹${((stats?.total_revenue || 0) / 1000).toFixed(1)}K`, icon: IndianRupee, color: 'from-orange-500 to-orange-600', change: '+12.5%' },
    { label: 'Total Customers', value: stats?.total_customers || 0, icon: Users, color: 'from-blue-500 to-blue-600', change: '+8.2%' },
    { label: 'Total Bookings', value: stats?.total_bookings || 0, icon: ShoppingBag, color: 'from-purple-500 to-purple-600', change: '+15.3%' },
    { label: 'Pending', value: stats?.pending_bookings || 0, icon: Clock, color: 'from-yellow-500 to-yellow-600', change: '-3.1%' },
    { label: 'Completed', value: stats?.completed_bookings || 0, icon: CheckCircle, color: 'from-green-500 to-green-600', change: '+22.4%' },
    { label: 'Avg Rating', value: '4.8', icon: Star, color: 'from-pink-500 to-pink-600', change: '+0.2' },
  ];

  const statusCounts = stats?.recent_bookings?.reduce((acc, b) => {
    acc[b.status] = (acc[b.status] || 0) + 1;
    return acc;
  }, {}) || {};

  const pieData = Object.entries(statusCounts).map(([k, v]) => ({ name: STATUS_LABELS[k], value: v, color: STATUS_COLORS[k] }));

  return (
    <div className="flex-1 overflow-auto">
      <Header title="Dashboard" subtitle={`Overview as of ${new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}`} />
      <div className="p-6 space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {statCards.map(({ label, value, icon: Icon, color, change }) => (
            <div key={label} className="card col-span-1">
              <div className={`w-10 h-10 bg-gradient-to-br ${color} rounded-xl flex items-center justify-center mb-3 shadow-lg`}>
                <Icon size={18} className="text-white" />
              </div>
              <p className="text-xs text-gray-500 font-medium mb-1">{label}</p>
              <p className="text-xl font-black text-gray-900">{value}</p>
              <p className={`text-xs font-semibold mt-1 ${change.startsWith('+') ? 'text-green-500' : 'text-red-500'}`}>{change} vs last month</p>
            </div>
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Revenue Chart */}
          <div className="card lg:col-span-2">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-bold text-gray-900">Revenue Overview</h3>
                <p className="text-xs text-gray-500">Monthly revenue trends</p>
              </div>
              <div className="flex items-center gap-1 text-green-500 text-sm font-semibold bg-green-50 px-2.5 py-1 rounded-full">
                <TrendingUp size={13} /> +18.4%
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={MONTHLY}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={v => `₹${v/1000}K`} />
                <Tooltip formatter={v => `₹${v.toLocaleString('en-IN')}`} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
                <Area type="monotone" dataKey="revenue" stroke="#f3700e" strokeWidth={2.5} fill="url(#orange)" />
                <defs>
                  <linearGradient id="orange" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f3700e" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#f3700e" stopOpacity={0} />
                  </linearGradient>
                </defs>
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Booking Status Pie */}
          <div className="card">
            <h3 className="font-bold text-gray-900 mb-5">Booking Status</h3>
            {pieData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={140}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={3} dataKey="value">
                      {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: '10px', border: 'none', fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2 mt-3">
                  {pieData.map(d => (
                    <div key={d.name} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                        <span className="text-xs text-gray-600">{d.name}</span>
                      </div>
                      <span className="text-xs font-bold text-gray-900">{d.value}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : <p className="text-sm text-gray-400 text-center py-8">No data yet</p>}
          </div>
        </div>

        {/* Recent Bookings */}
        <div className="card">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-bold text-gray-900">Recent Bookings</h3>
            <a href="/bookings" className="text-sm text-orange-500 font-semibold hover:underline">View All</a>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="table-header">ID</th>
                  <th className="table-header">Customer</th>
                  <th className="table-header">Service</th>
                  <th className="table-header">Date</th>
                  <th className="table-header">Amount</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Payment</th>
                </tr>
              </thead>
              <tbody>
                {(stats?.recent_bookings || []).map(b => (
                  <tr key={b.id} className="border-b border-gray-50 hover:bg-gray-50/50">
                    <td className="table-cell font-mono text-xs text-gray-400">#{b.id}</td>
                    <td className="table-cell font-semibold text-gray-900">{b.customer_name}</td>
                    <td className="table-cell text-gray-600">{b.service_detail?.name}</td>
                    <td className="table-cell text-gray-500 text-xs">{new Date(b.scheduled_date).toLocaleDateString('en-IN')}</td>
                    <td className="table-cell font-bold text-gray-900">₹{b.amount}</td>
                    <td className="table-cell">
                      <span className="badge" style={{ backgroundColor: STATUS_COLORS[b.status] + '20', color: STATUS_COLORS[b.status] }}>
                        {STATUS_LABELS[b.status]}
                      </span>
                    </td>
                    <td className="table-cell">
                      <span className={`badge ${b.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                        {b.payment_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!stats?.recent_bookings?.length && (
              <p className="text-center text-gray-400 text-sm py-8">No bookings yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
